import React, { useContext, useMemo, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import TextFieldCustom from '../../../components/TextField/TextFieldCustom';
import Accordion from '../../../components/Accordion/Accordion';
import DatePicker from '../../../components/DatePicker/DatePicker';
import SelectCustom from '../../../components/Select/SelectCustom';
import SingleDropdown from '../../../components/Select/SingleDropdown';
import { FIELDTYPE } from '../../../constants/field-type';
import RadioButton from '../../../components/RadioButton/RadioButton';
import { getFileICon, isEmptyObject } from '../../../utils/utilities';
import { agent, buyer, cobroke, commissionAmount, commissionRate, landlord, partiesKeywords, tenant } from '../specialFields';
import ButtonCustom from '../../../components/Button/ButtonCustom';
import Divider from '../../../components/Divider/Divider';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import FormContext from '../../../context/form-context';
import { setFormState, setPartiesFields } from '../../../state/action';
import MultipleSelectV2 from '../../../components/Select/MultiSelectV2';
import FloatingButton from '../../../components/FloatingButton/FloatingButton'
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UploadButton from '../../../components/Button/UploadButton';
import { Link } from '@mui/material';
// import SaveIcon from '@mui/icons-material/Save';

const FormPage = ({
    sections,
    fields = [],
    formState,
    formErrors,
    handleFieldChange,
    handleFileUploadChange
}) => {
    const { appState, dispatch } = useContext(FormContext)
    const { partiesFieldsCount } = appState;
    const min = 1;
    const max = 8;
    
    const btnAction = {
        addFields: (secName) => {
            const field = getAddedField(secName)

            if (partiesFieldsCount[field] < max) {
                dispatch(setPartiesFields({
                    ...partiesFieldsCount,
                    [field]: partiesFieldsCount[field] + 1
                }))
            }
        },
        removeFields: (secName) => {
            const field = getAddedField(secName)

            if (partiesFieldsCount[field] > min) {
                dispatch(setPartiesFields({
                    ...partiesFieldsCount,
                    [field]: partiesFieldsCount[field] - 1
                }))

                const newMainValues = appState.formState.mainValues;
                //remove in state object
                const filteredObject = removeKeys(newMainValues, field, partiesFieldsCount[field]);
                dispatch(setFormState({
                    ...appState.formState,
                    mainValues: filteredObject
                }))
            }
        }
    }

    const removeKeys = (obj, field, num) => {
        const newObj = {};
        for (const key in obj) {
            //field party with number e.g (buyer1 , seller1, purchaser1)
            if (!key.includes(field + num)) {
                newObj[key] = obj[key];
            }
        }
        return newObj;
    };

    const getAddedField = (secName) => {
        const sectionName = secName.toLowerCase();
        for (const party of partiesKeywords) {
            if (sectionName.includes(party)) {
                return party //return "buyer" , "seller" etc
            }
        }
        return ""; //default value
    };

    const renderFields = useMemo(
        () => (field, section) => {
            const fieldValue = formState[field.FieldName2];
            const fieldError = formErrors[field.FieldName2];

            if (!isEmptyObject(section)) {
                const filteredField = filterFields(field, section);
                if (!filteredField) return null;
            }

            let commonProps = {
                label: field.FieldName,
                name: field.FieldName2,
                size: field.FieldSize,
                setValue: (val) => handleFieldChange(val, field.FieldName2),
                value: fieldValue,
                withInfo: field.ShowDescription === "Yes" ? true : false ,
                description: field.Description,
                required: field.IsRequired,
                error: !!fieldError,
                errorMsg: fieldError || "",
                disabled: field.Disabled,
                withFormula: field.Formula || "",
                placeholder: field.Placeholder || ""
            }

            switch (field.FieldType) {
                case FIELDTYPE.TEXT:
                    return <TextFieldCustom {...commonProps} key={field.FieldName2} />
                case FIELDTYPE.LONG_TEXT:
                    return <TextFieldCustom {...commonProps} multiline={true} key={field.FieldName2} />
                case FIELDTYPE.NUMBER:

                    if (
                        (field.FieldName2.includes(cobroke.commissionRate)
                            && formState?.[cobroke.commissionType] !== "%")
                        ||
                        (field.FieldName2.includes(agent.commissionRate)
                            && formState?.[agent.commissionType] !== "%")
                        ||
                        (field.FieldName2.includes(buyer.commissionRate)
                            && formState?.[buyer.commissionType] !== "%")
                        ||
                        (field.FieldName2.includes(tenant.commissionRate)
                            && formState?.[tenant.commissionType] !== "%")
                        ||
                        (field.FieldName2.includes(landlord.commissionRate)
                            && formState?.[landlord.commissionType] !== "%")
                    ) {
                        return <Divider
                            size={field.FieldSize}
                            key={field.FieldName2}
                            label={`Commission Rate (%): <br/> <strong> ${fieldValue ? parseFloat(fieldValue).toString() : ""}% </strong>`}
                        />
                    }

                    return <TextFieldCustom {...commonProps} type={field.FieldType} key={field.FieldName2} />

                case FIELDTYPE.SINGLE_SELECT:
                    return <SingleDropdown {...commonProps} options={field.FieldOptions} key={field.FieldName2} />

                case FIELDTYPE.CURRENCY:

                    if (
                        (field.FieldName2 === cobroke.commissionAmount
                            && formState?.[cobroke.commissionType] !== "Amount")
                        ||
                        (field.FieldName2 === agent.commissionAmount
                            && formState?.[agent.commissionType] !== "Amount") 
                        ||
                        (field.FieldName2 === buyer.commissionAmount
                            && formState?.[buyer.commissionType] !== "Amount") 
                        ||
                        (field.FieldName2 === tenant.commissionAmount
                            && formState?.[tenant.commissionType] !== "Amount") 
                        ||
                        (field.FieldName2 === landlord.commissionAmount
                            && formState?.[landlord.commissionType] !== "Amount") 
                    ) {
                        return <Divider
                            size={field.FieldSize}
                            key={field.FieldName2}
                            label={`Commission Amount: <br/> <strong>$ ${ fieldValue || '' } </strong>`}
                        />
                    }

                    return <TextFieldCustom {...commonProps} type={field.FieldType} key={field.FieldName2} />;
                case FIELDTYPE.DROP_DOWN:
                    return <SelectCustom {...commonProps} options={field.FieldOptions} key={field.FieldName2} />
                case FIELDTYPE.MULTI_SELECT:
                    return <MultipleSelectV2 {...commonProps} options={field.FieldOptions} key={field.FieldName2} />
                case FIELDTYPE.DATE:
                    return <DatePicker {...commonProps} key={field.FieldName2} />
                case FIELDTYPE.RADIO:
                    return <RadioButton {...commonProps} options={field.FieldOptions} key={field.FieldName2} />
                case FIELDTYPE.FILE:
                    return (

                    <Grid key={field.FieldName2} xs={12} sm={12} md={12} lg={12}  >
                    <UploadButton 
                        label={`Upload ${field.FieldName}`} 
                        acceptableFile=".pdf, .xls, .xlsx, .doc, .docx, image/*"
                        variant={"contained"}
                        sx={{ my: 1, fontSize: 11 }}
                        color={"success"}
                        onFileUpload={(file)=>handleFileUploadChange(file , field.FieldName2)}
                        disabled={field.Disabled}
                    />
                    {fieldValue &&
                    <>
                                <img src={getFileICon((fieldValue?.name))}
                                    alt="Download Icon"
                                    style={{ width: '20px', height: '20px', marginLeft: 30 }}
                                />
                                <Link
                                    // href={fieldValue?.link} 
                                    onClick={ () => fieldValue?.link ?
                                         window.open(`${fieldValue?.link}#toolbar=0&navpanes=0&scrollbar=0`, '_blank', 'width=800,height=800') 
                                        :
                                        ""}
                                    underline="hover"
                                    sx={{  mt: 3, fontSize: 15 }}
                                >
                                    { fieldValue?.link ? fieldValue?.name : `${fieldValue?.name} (this will be renamed to ${field?.FieldName2})`}
                                </Link>
                    </>
                        
                    }
                    </Grid>
                )

                case FIELDTYPE.DIVIDER:
                    return <Divider size={field.FieldSize} key={field.FieldName2} label={field.Description} />
                default:
                    return null;
            }

        }, [formState, formErrors, handleFieldChange, partiesFieldsCount]);


    const filterFields = (field, section) => {
        const sectionName = section.SecName?.toLowerCase();

        const isPartiesField = partiesKeywords.some(keyword =>
            field.FieldName2?.toLowerCase().includes(keyword) //checking on the actual field name if has number e.g("Buyer (1) Name")
            &&
            !field.FieldName2?.toLowerCase().includes("associate")
        );

        if (isPartiesField) {
            if (sectionName.includes(getAddedField(sectionName))) {
                if(field.FieldName2 && field.FieldName2.match(/\d+/) && field.FieldType !== FIELDTYPE.DIVIDER){
                    if ( field.FieldName2.match(/\d+/)[0] > partiesFieldsCount[getAddedField(sectionName)]) {
                        return null;
                    }
                }
                
           
            }
        }

        if (!isEmptyObject(section)) {
            if (field.FieldSection !== section.SecNumber) return null;
        }

        return field;
    };

    // used for Form Submit
    // const handleSubmit= (e) => {
    //     e.preventDefault();
    //     const form = new FormData(e.target);
    //     const data = Object.fromEntries(form.entries());
    //     // handleFieldChange(data);
    //     console.log(data)
    // }
    return (
        <>
            <>
                {/* <form id={'mainForm'} onSubmit={handleSubmit}>   */}
                {sections && sections.length > 0 ?
                    sections.map((section, idx) => {
                        return (
                            <Accordion
                                sectionName={section.SecName}
                                idx={idx}
                                expanded={true}
                                key={idx}
                            >
                                {partiesKeywords.some(keyword =>
                                    section.SecName?.toLowerCase().includes(keyword)
                                ) &&
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <ButtonCustom
                                            onClickBtn={() => btnAction.addFields(section.SecName)}
                                            label={'add ' + getAddedField(section.SecName)}
                                            endIcon={<AddCircleOutlineOutlinedIcon />}
                                            variant='contained'
                                            sx={{ mx: 2, mb: 2 }}
                                            disabled={partiesFieldsCount[getAddedField(section.SecName)] === max}
                                        />
                                        <ButtonCustom
                                            onClickBtn={() => btnAction.removeFields(section.SecName)}
                                            label={"Remove"}
                                            endIcon={<RemoveCircleOutlineOutlinedIcon />}
                                            variant='outlined'
                                            sx={{ mx: 2, mb: 2 }}
                                            color="error"
                                            disabled={partiesFieldsCount[getAddedField(section.SecName)] <= min}
                                        />
                                    </div>
                                }
                                <Grid container spacing={2}>
                                    {fields.length > 0 &&
                                        fields.map((field) =>
                                            renderFields(field, section)
                                        )
                                    }
                                </Grid>

                            </Accordion>
                        )
                    })
                    :
                    <Accordion
                        sectionName={""}
                        idx={1}
                        expanded={true}
                        key={1}
                    >
                        <Grid container spacing={2}>
                            {fields.length > 0 &&
                                fields.map((field) =>
                                    renderFields(field, {})
                                )
                            }
                        </Grid>
                    </Accordion>
                }
                {/* </form> */}
            </>
            <FloatingButton bottomPosition={110} rightPosition={25}>
                <Fab aria-label="scroll back to top" sx={{ color: '#1976d2', p: 3 }}>
                    <KeyboardArrowUpIcon />
                </Fab>
            </FloatingButton>
        </>

    );
}




export default FormPage;